import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button } from 'antd'
import {
    ACTIVITY_CHART_COLORS,
    activitySeries,
    renderActivityChart,
    fullViewport,
    zoomViewport,
    panViewport,
    isFullView,
    formatViewportRange,
    nearestSample,
    timeAtX,
} from './activityCharts'
import { niceYMax, formatTimeTick } from './playerChart'
import { decimateSeries } from './playerLogic'

const PANELS = [
    { key: 'hr', caption: 'Пульс', color: ACTIVITY_CHART_COLORS.hr, minPad: 5 },
    { key: 'power', caption: 'Мощность', color: ACTIVITY_CHART_COLORS.power, minPad: 0 },
]

const CHART_HEIGHT = 140

function ChartPanel({ caption, color, series, view, yMin, yMax, checkpointTime, checkpointLabel, onWheel, onReset, onPointerDown, onPointerMove, onPointerUp }) {
    const wrapRef = useRef(null)
    const canvasRef = useRef(null)

    const draw = useCallback(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        renderActivityChart(canvas, { series, color, view, yMin, yMax, checkpointTime, checkpointLabel })
    }, [series, color, view, yMin, yMax, checkpointTime, checkpointLabel])

    useEffect(() => { draw() }, [draw])

    // Re-measure + redraw on container resize (the canvas sizes itself from
    // the wrapper, so a resize is just another redraw, like PlayerPowerChart).
    const drawRef = useRef(draw)
    drawRef.current = draw
    useEffect(() => {
        const el = wrapRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(() => {
            if (drawRef.current) drawRef.current()
        })
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    // React 17+ registers onWheel as a passive root listener, so preventDefault
    // inside a synthetic handler is ignored (the page would scroll while
    // zooming). Attach a native non-passive wheel listener per canvas instead.
    const wheelRef = useRef(null)
    wheelRef.current = (e) => { if (onWheel) onWheel(e) }
    useEffect(() => {
        const el = canvasRef.current
        if (!el) return
        const onNativeWheel = (e) => {
            e.preventDefault()
            if (wheelRef.current) wheelRef.current(e)
        }
        el.addEventListener('wheel', onNativeWheel, { passive: false })
        return () => el.removeEventListener('wheel', onNativeWheel)
    }, [])

    return (
        <div>
            <div style={{ color: ACTIVITY_CHART_COLORS.label, fontSize: 12, marginTop: 8, marginBottom: 2 }}>
                {caption}
            </div>
            <div ref={wrapRef} style={{ position: 'relative', height: CHART_HEIGHT }}>
                <canvas
                    ref={canvasRef}
                    style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        display: 'block', touchAction: 'none', userSelect: 'none',
                        WebkitUserSelect: 'none', cursor: 'crosshair',
                    }}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onDoubleClick={onReset}
                    onContextMenu={(e) => e.preventDefault()}
                />
            </div>
        </div>
    )
}

export default function ActivityCharts() {
    const { selectedActivity, selectedActivityTrace } = useSelector(state => state.activities)

    const allSeries = useMemo(() => ({
        hr: activitySeries(selectedActivityTrace, 'hr'),
        power: activitySeries(selectedActivityTrace, 'power'),
    }), [selectedActivityTrace])

    const duration = useMemo(() => {
        let max = 0
        for (const s of [allSeries.hr, allSeries.power]) {
            if (s.length) max = Math.max(max, s[s.length - 1].time)
        }
        return Math.max(1, max)
    }, [allSeries])

    // A shared viewport drives both charts. `null` means "full ride"; any
    // duration change (initial trace load) resets the window to full. The
    // parent remounts this component per activity (key={selectedActivity.id}),
    // so no zoom state leaks between activities.
    const [view, setView] = useState(null)
    useEffect(() => { setView(null) }, [duration])

    // Shared checkpoint time (seconds, relative). A left click places it, and
    // dragging moves it live over both charts.
    const [checkpoint, setCheckpoint] = useState(null)
    useEffect(() => { setCheckpoint(null) }, [duration])

    const activeView = view || fullViewport(duration)
    const viewRef = useRef(activeView)
    viewRef.current = activeView

    const panels = useMemo(() => {
        const out = []
        for (const p of PANELS) {
            const series = allSeries[p.key]
            if (!series.length) continue
            let peak = 0
            let valley = Infinity
            for (const pt of series) {
                if (pt.value > peak) peak = pt.value
                if (pt.value < valley) valley = pt.value
            }
            out.push({
                key: p.key,
                caption: p.caption,
                color: p.color,
                series,
                yMax: niceYMax(peak),
                yMin: p.minPad > 0 ? Math.max(0, valley - p.minPad) : 0,
            })
        }
        return out
    }, [allSeries])

    const handleWheel = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect()
        if (!rect.width) return
        const anchorTime = timeAtX(rect, e.clientX, viewRef.current)
        if (anchorTime == null) return
        const factor = Math.pow(1.2, e.deltaY / 100)
        setView(zoomViewport(viewRef.current, { factor, anchorTime, duration }))
    }, [duration])

    const timeAtPointer = useCallback((el, clientX) => {
        return timeAtX(el.getBoundingClientRect(), clientX, viewRef.current)
    }, [])

    // Left button: default places/drags the checkpoint; Shift+drag or the
    // right button pans the window instead.
    const pointerRef = useRef(null)

    const handlePointerDown = useCallback((e) => {
        if (e.button != null && e.button !== 0 && e.button !== 2) return
        const el = e.currentTarget
        const t = timeAtPointer(el, e.clientX)
        if (t == null) return
        const mode = e.shiftKey || e.button === 2 ? 'pan' : 'checkpoint'
        pointerRef.current = { mode, active: true, lastX: e.clientX }
        if (el.setPointerCapture) {
            try { el.setPointerCapture(e.pointerId) } catch (err) { /* ignore */ }
        }
        if (mode === 'checkpoint') {
            setCheckpoint(t)
        } else {
            el.style.cursor = 'grabbing'
        }
    }, [timeAtPointer])

    const handlePointerMove = useCallback((e) => {
        const p = pointerRef.current
        if (!p || !p.active) return
        const el = e.currentTarget
        if (p.mode === 'pan') {
            const rect = el.getBoundingClientRect()
            if (!rect.width) return
            const dx = e.clientX - p.lastX
            p.lastX = e.clientX
            const v = viewRef.current
            const span = v.end - v.start
            setView(panViewport(v, { deltaSeconds: -(dx / rect.width) * span, duration }))
        } else {
            const t = timeAtPointer(el, e.clientX)
            if (t != null) setCheckpoint(t)
        }
    }, [duration, timeAtPointer])

    const handlePointerUp = useCallback((e) => {
        if (pointerRef.current) pointerRef.current.active = false
        const el = e.currentTarget
        if (el.releasePointerCapture && e.pointerId != null) {
            try { el.releasePointerCapture(e.pointerId) } catch (err) { /* ignore */ }
        }
        el.style.cursor = ''
    }, [])

    const handleReset = useCallback(() => setView(null), [])

    // Exact HR/PWR values at the checkpoint, looked up on the full (non-
    // decimated, non-sliced) series so the tooltip is precise at any zoom.
    const checkpointLabel = useMemo(() => {
        if (checkpoint == null) return null
        const hr = nearestSample(allSeries.hr, checkpoint)
        const pwr = nearestSample(allSeries.power, checkpoint)
        const hrText = hr != null ? String(Math.round(hr.value)) : '—'
        const pwrText = pwr != null ? String(Math.round(pwr.value)) : '—'
        return `${formatTimeTick(checkpoint)} — HR ${hrText} | PWR ${pwrText}`
    }, [checkpoint, allSeries])

    if (!selectedActivity || panels.length === 0) return null

    const displayedPanels = []
    for (const p of panels) {
        const data = decimateSeries(p.series.filter(pt => pt.time >= activeView.start - 1 && pt.time <= activeView.end + 1))
        displayedPanels.push({ ...p, data })
    }
    if (!displayedPanels.some(p => p.data.length > 0)) return null

    return (
        <div>
            {displayedPanels.map(p => (
                <ChartPanel
                    key={p.key}
                    caption={p.caption}
                    color={p.color}
                    series={p.data}
                    view={activeView}
                    yMin={p.yMin}
                    yMax={p.yMax}
                    checkpointTime={checkpoint}
                    checkpointLabel={checkpointLabel}
                    onWheel={handleWheel}
                    onReset={handleReset}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                />
            ))}
            {/* Footer row: the hint stays left, and when zoomed the range + «Показать всё» appears at the far right inline. The row height is fixed so toggling zoom never reflows the details layout (no map resizing/overlap). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 24, marginTop: 4 }}>
                <span style={{ color: ACTIVITY_CHART_COLORS.label, fontSize: 11, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    Клик/перетаскивание — метка; правая кнопка+перетаскивание — прокрутка; колесо мыши — масштаб; двойной клик — показать всё
                </span>
                {!isFullView(activeView, duration) && (
                    <>
                        <span style={{ color: ACTIVITY_CHART_COLORS.label, fontSize: 12, whiteSpace: 'nowrap' }}>
                            {formatViewportRange(activeView)}
                        </span>
                        <Button size="small" onClick={handleReset}>Показать всё</Button>
                    </>
                )}
            </div>
        </div>
    )
}